import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppLayout from "../components/AppLayout";
import PlanBanner from "../components/PlanBanner";
import api from "../services/api";
import { statusLabel } from "../lib/format";

export default function MyRadios() {
  const { t } = useTranslation();
  const [radios, setRadios] = useState([]);
  const [plan, setPlan] = useState(null);
  const [listeners, setListeners] = useState({}); // { [radioId]: number }
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [radiosRes] = await Promise.all([
        api.get("/api/radios"),
        api.get("/api/me/plan").then((r) => setPlan(r.data)).catch(() => {}),
      ]);
      const list = radiosRes.data || [];
      setRadios(list);
      // Auditeurs en direct (now-playing) par radio.
      list.forEach((r) => {
        api.get(`/api/radios/${r.id}/nowplaying`)
          .then((np) => setListeners((prev) => ({ ...prev, [r.id]: np.data?.listeners?.current ?? 0 })))
          .catch(() => {});
      });
    } catch {
      setRadios([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm(t("myRadios.confirmDelete"))) return;
    try {
      await api.delete(`/api/radios/${id}`);
      setRadios((prev) => prev.filter((r) => r.id !== id));
      api.get("/api/me/plan").then((r) => setPlan(r.data)).catch(() => {});
    } catch { /* ignore */ }
  };

  const atLimit = plan && (plan.usage?.radios ?? 0) >= (plan.quota?.maxRadios ?? 0);

  const createBtn = atLimit ? (
    <a
      href="/#pricing"
      title={t("myRadios.upgradeTitle")}
      className="bg-amber-100 text-amber-800 px-4 py-2 rounded-md text-sm hover:bg-amber-200"
    >
      {t("myRadios.upgrade")}
    </a>
  ) : (
    <button
      onClick={() => navigate("/creer-radio/step1")}
      className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md text-sm"
    >
      ＋ {t("myRadios.create")}
    </button>
  );

  return (
    <AppLayout title={t("myRadios.title")} actions={createBtn}>
      {!loading && <PlanBanner plan={plan} />}

      {loading ? (
        <p className="text-gray-500">{t("myRadios.loading")}</p>
      ) : radios.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
          <p className="text-gray-600 mb-4">{t("myRadios.empty")}</p>
          {!atLimit && (
            <button onClick={() => navigate("/creer-radio/step1")} className="bg-purple-600 hover:bg-purple-700 text-white px-5 py-2 rounded-md">
              {t("myRadios.createFirst")}
            </button>
          )}
        </div>
      ) : (
        <div className="grid gap-3">
          {radios.map((r) => {
            const st = statusLabel(r.status, t);
            return (
              <div key={r.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-4">
                {r.logo ? (
                  <img src={r.logo} alt="" className="w-14 h-14 rounded-xl object-cover bg-gray-100" onError={(e) => { e.currentTarget.src = "/microphone.png"; }} />
                ) : (
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 text-white flex items-center justify-center text-2xl">📻</div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-gray-900 truncate">{r.name}</div>
                  <div className="text-xs text-gray-500 truncate">
                    {[r.genre, r.language].filter(Boolean).join(" • ")}
                  </div>
                </div>
                <span className="text-xs text-gray-500 hidden sm:flex items-center gap-1">
                  🎧 {listeners[r.id] ?? 0}
                </span>
                <span className={`text-xs px-2 py-1 rounded-full ${st.cls}`}>{st.text}</span>
                <Link to={`/radio/${r.id}`} className="text-sm bg-purple-50 text-purple-700 px-3 py-2 rounded-md hover:bg-purple-100">{t("myRadios.studio")}</Link>
                <button onClick={() => remove(r.id)} className="w-9 h-9 rounded-full bg-gray-100 hover:bg-red-100 text-gray-600 hover:text-red-600 flex items-center justify-center">✕</button>
              </div>
            );
          })}
        </div>
      )}
    </AppLayout>
  );
}
